// DOM-based rendering for game boards and UI elements

import { GRID_SIZE, gameState } from './game.ts';
import type { Board, Ship, ClientFaction, AbilitiesMap, EnemyShotMark, GameMode } from './game.ts';
import type { Coord } from '../../shared/abilityLogic.ts';
import { shipGraphicsManager } from './shipGraphics.js';

/** Default #toast durations (ms) for info and error toasts. */
export const TOAST_MS = 3000;
export const TOAST_ERROR_MS = 4500;

// ─── Renderer-facing structural types ────────────────────────────

/**
 * Faction colors as the renderer consumes them. Looser than the wire
 * FactionColors: `glow` is optional (callers pass hand-built fallbacks
 * without it) and the renderer derives a glow from `primary` when absent.
 */
export interface FactionStyleColors {
    primary: string;
    secondary: string;
    glow?: string;
}

/** Anything sunk-ship-shaped enough to animate (Ship, or a server-cells wrapper). */
export interface SunkShipView {
    getCells(): Coord[];
}

/** Ship-status entries (real Ships, or MP opponent-tracking adapters). */
export interface ShipStatusView {
    class: string;
    isSunk(): boolean;
}

/** Callbacks wired by setupPlacementBoard and reused by drag handlers. */
interface PlacementCallbacks {
    onDrop: (x: number, y: number) => void;
    onCellClick: (x: number, y: number) => void;
    onCellRightClick: (x: number, y: number) => void;
    canPlace: (ship: Ship, x: number, y: number) => boolean;
}

/** Stats panel payload for the game-over screen (battlePhase.endGame). */
export interface GameOverStats {
    shotsFired: number;
    accuracy: number;
    shipsLost: number;
    turns?: number;
    duration?: string;
    score?: number | null;
    starRating?: number | null;
    isBlitz?: boolean;
    isTimeTrial?: boolean;
    durationSeconds?: number;
}

/** Winner/loser match panel payload for the game-over screen. */
export interface GameOverPlayerInfo {
    mode?: GameMode | null;
    playerName: string;
    playerFactionId: string | null;
    playerFactionName: string | null;
    opponentName: string;
    opponentFactionId: string | null;
    opponentFactionName: string | null;
}

// Reading offsetHeight forces a synchronous reflow so a re-added animation
// class restarts its animation.
function forceReflow(el: HTMLElement): number {
    return el.offsetHeight;
}

class Renderer {
    declare placementBoard: HTMLElement | null;
    declare yourBoard: HTMLElement | null;
    declare enemyBoard: HTMLElement | null;

    // Faction colors for ship graphics
    declare playerFactionColors: FactionStyleColors | null;
    declare enemyFactionColors: FactionStyleColors | null;

    // Custom drag state
    declare draggedShip: Ship | null;
    declare draggedShipElement: HTMLElement | null;
    declare ghostElement: HTMLDivElement | null;
    declare isDragging: boolean;
    declare dragStartPos: { x: number; y: number };
    declare currentCell: { x: number; y: number } | null;
    declare placementCallbacks: PlacementCallbacks | null;
    declare isTouchDevice: boolean;
    declare _keydownAbortController: AbortController | null;

    // Game-over screen waits for running sink cascades so the final kill is seen
    declare sinkAnimationsEndAt: number;
    declare pendingScreenTimer: ReturnType<typeof setTimeout> | null;
    // Hide timer of the current #toast (cleared on every showToast)
    declare toastTimer: ReturnType<typeof setTimeout> | null;

    // Assigned outside the constructor by drag/board setup methods
    declare onDragEndCallback?: () => void;
    declare pointerHandlersAttached?: boolean;
    declare selectedFromDock?: Ship | null;
    declare selectedFromDockElement?: HTMLElement | null;
    declare selectedShipForMove?: Ship | null;
    declare selectedShipPointerHandler?: ((e: PointerEvent) => void) | null;
    declare _placementBoardAbort?: AbortController;
    declare _enemyBoardAbort?: AbortController;

    constructor() {
        this.sinkAnimationsEndAt = 0;
        this.pendingScreenTimer = null;
        this.toastTimer = null;
        this.placementBoard = null;
        this.yourBoard = null;
        this.enemyBoard = null;

        // Faction colors for ship graphics
        this.playerFactionColors = null;
        this.enemyFactionColors = null;

        // Custom drag state
        this.draggedShip = null;
        this.draggedShipElement = null;
        this.ghostElement = null;
        this.isDragging = false;
        this.dragStartPos = { x: 0, y: 0 };
        this.currentCell = null;
        this.placementCallbacks = null;
        this.isTouchDevice = false;
        this._keydownAbortController = null;
    }

    // Initialize board elements
    initializeBoards(): void {
        this.placementBoard = document.getElementById('placement-board');
        this.yourBoard = document.getElementById('your-board');
        this.enemyBoard = document.getElementById('enemy-board');

        // Create grids
        this.createGrid(this.placementBoard!);
        this.createGrid(this.yourBoard!);
        this.createGrid(this.enemyBoard!);

        // Wrap boards with coordinate labels
        this.wrapBoardWithCoordinates(this.placementBoard);
        this.wrapBoardWithCoordinates(this.yourBoard);
        this.wrapBoardWithCoordinates(this.enemyBoard);
    }

    // Wrap a board element with coordinate labels (A-O columns, 1-15 rows)
    wrapBoardWithCoordinates(boardElement: HTMLElement | null): void {
        if (!boardElement) return;

        // Skip if already wrapped (prevents issues if called multiple times)
        if ((boardElement.parentNode as HTMLElement | null)?.classList.contains('board-with-coords')) return;

        // Column labels: A-O (15 letters)
        const columnLabels = 'ABCDEFGHIJKLMNO'.split('');
        // Row labels: 1-15
        const rowLabels = Array.from({ length: GRID_SIZE }, (_, i) => i + 1);

        // Create wrapper
        const wrapper = document.createElement('div');
        wrapper.className = 'board-with-coords';

        // Create corner element (empty top-left)
        const corner = document.createElement('div');
        corner.className = 'coord-corner';
        wrapper.appendChild(corner);

        // Create column header row
        const columns = document.createElement('div');
        columns.className = 'coord-columns';
        for (const label of columnLabels) {
            const span = document.createElement('span');
            span.className = 'coord-label';
            span.textContent = label;
            columns.appendChild(span);
        }
        wrapper.appendChild(columns);

        // Create row labels container
        const rows = document.createElement('div');
        rows.className = 'coord-rows';
        for (const label of rowLabels) {
            const span = document.createElement('span');
            span.className = 'coord-label';
            span.textContent = String(label);
            rows.appendChild(span);
        }
        wrapper.appendChild(rows);

        // Insert wrapper and move board into it
        boardElement.parentNode!.insertBefore(wrapper, boardElement);
        wrapper.appendChild(boardElement);
    }

    // Create a 15x15 grid of cells
    createGrid(container: HTMLElement): void {
        container.innerHTML = ''; // Safe: clearing container, no user content
        for (let y = 0; y < GRID_SIZE; y++) {
            for (let x = 0; x < GRID_SIZE; x++) {
                const cell = document.createElement('div');
                cell.className = 'cell';
                cell.dataset.x = String(x);
                cell.dataset.y = String(y);
                cell.setAttribute('role', 'gridcell');
                cell.setAttribute('aria-label', `${String.fromCharCode(65 + x)}${y + 1}`);
                container.appendChild(cell);
            }
        }
    }

    // Render ship list in the dock
    renderShipList(
        ships: Ship[],
        onDragStart: (ship: Ship, element: HTMLElement) => void,
        onDragEnd: () => void
    ): void {
        const shipList = document.getElementById('ship-list')!;
        shipList.innerHTML = '';

        // Detect touch device
        this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        // Check if silhouette mode is available
        const useSilhouettes = shipGraphicsManager.isSilhouetteMode() && this.playerFactionColors;

        for (const ship of ships) {
            const item = document.createElement('div');
            item.className = `ship-item ${ship.placed ? 'placed' : ''}`;
            item.dataset.shipId = ship.id;

            let preview: HTMLElement | null = null;

            if (useSilhouettes) {
                // Use silhouette preview
                preview = shipGraphicsManager.createDockPreviewElement(ship, this.playerFactionColors);
            }

            if (!preview) {
                // Fall back to cell-based preview
                preview = document.createElement('div');
                preview.className = 'ship-preview';
                for (let i = 0; i < ship.size; i++) {
                    const cell = document.createElement('div');
                    cell.className = 'ship-cell';
                    preview.appendChild(cell);
                }
            }

            const name = document.createElement('span');
            name.className = 'ship-name';
            name.textContent = ship.name;

            item.appendChild(preview);
            item.appendChild(name);

            // Track last tap time for double-tap detection
            let lastTapTime = 0;

            // Pointer events for cross-platform drag
            item.addEventListener('pointerdown', (e) => {
                if (ship.placed) return;
                e.preventDefault();

                // Detect double-tap to rotate
                const now = Date.now();
                if (now - lastTapTime < 300) {
                    // Double tap - rotate ship
                    ship.rotate();
                    this.updateShipPreviewOrientation(item, ship.horizontal, ship);
                    lastTapTime = 0;
                    return;
                }
                lastTapTime = now;

                this.startDrag(e, ship, item, onDragStart);
            });

            shipList.appendChild(item);
        }

        // Store callbacks for use in drag handlers
        this.onDragEndCallback = onDragEnd;

        // Global pointer move/up handlers (only add once)
        if (!this.pointerHandlersAttached) {
            document.addEventListener('pointermove', (e) => this.onPointerMove(e));
            document.addEventListener('pointerup', (e) => this.onPointerUp(e));
            document.addEventListener('pointercancel', (e) => this.onPointerUp(e));
            this.pointerHandlersAttached = true;
        }
    }

    // Start dragging a ship
    startDrag(
        e: PointerEvent,
        ship: Ship,
        element: HTMLElement,
        onDragStart: (ship: Ship, element: HTMLElement) => void
    ): void {
        // Clear any previous selection first
        this.clearDockSelection();

        this.isDragging = true;
        this.draggedShip = ship;
        this.draggedShipElement = element;
        this.dragStartPos = { x: e.clientX, y: e.clientY };

        // Mark element as dragging
        element.classList.add('dragging');

        // Create ghost element
        this.createGhost(ship, e.clientX, e.clientY);

        // Show rotate button on touch devices
        if (this.isTouchDevice) {
            this.showRotateButton(true);
        }

        // Call the original drag start callback
        onDragStart(ship, element);

        // Capture pointer for smoother tracking
        element.setPointerCapture(e.pointerId);
    }

    // Create visual ghost element
    createGhost(ship: Ship, x: number, y: number): void {
        this.removeGhost();

        const ghost = document.createElement('div');
        ghost.className = `ship-ghost ${ship.horizontal ? 'horizontal' : 'vertical'}`;
        ghost.id = 'ship-ghost';

        for (let i = 0; i < ship.size; i++) {
            const cell = document.createElement('div');
            cell.className = 'ghost-cell';
            ghost.appendChild(cell);
        }

        document.body.appendChild(ghost);
        this.ghostElement = ghost;

        this.updateGhostPosition(x, y);
    }

    // Update ghost position
    updateGhostPosition(x: number, y: number): void {
        if (!this.ghostElement || !this.draggedShip) return;

        const ghost = this.ghostElement;
        const offsetX = this.draggedShip.horizontal ?
            (this.draggedShip.size * 30) / 2 : 15;
        const offsetY = this.draggedShip.horizontal ?
            15 : (this.draggedShip.size * 30) / 2;

        ghost.style.left = `${x - offsetX}px`;
        ghost.style.top = `${y - offsetY}px`;
    }

    // Update ghost orientation
    updateGhostOrientation(horizontal: boolean): void {
        if (!this.ghostElement) return;
        this.ghostElement.classList.toggle('horizontal', horizontal);
        this.ghostElement.classList.toggle('vertical', !horizontal);
    }

    // Set ghost validity state
    setGhostValid(valid: boolean): void {
        if (!this.ghostElement) return;
        this.ghostElement.classList.toggle('invalid', !valid);
    }

    // Remove ghost element
    removeGhost(): void {
        if (this.ghostElement) {
            this.ghostElement.remove();
            this.ghostElement = null;
        }
    }

    // Handle pointer move
    onPointerMove(e: PointerEvent): void {
        if (!this.isDragging || !this.draggedShip) return;

        this.updateGhostPosition(e.clientX, e.clientY);

        // Find cell under pointer
        const cell = this.getCellAtPosition(e.clientX, e.clientY);
        if (cell && this.placementCallbacks) {
            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);

            if (this.currentCell?.x !== x || this.currentCell?.y !== y) {
                this.currentCell = { x, y };
                const valid = this.placementCallbacks.canPlace(this.draggedShip, x, y);
                this.showPlacementPreview(this.draggedShip, x, y, valid);
                this.setGhostValid(valid);
            }
        } else {
            this.clearPlacementPreview();
            this.currentCell = null;
        }
    }

    // Handle pointer up
    onPointerUp(e: PointerEvent): void {
        if (!this.isDragging) return;

        const cell = this.getCellAtPosition(e.clientX, e.clientY);

        if (cell && this.placementCallbacks) {
            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);
            this.placementCallbacks.onDrop(x, y);
            this.endDrag(true); // Ship was placed
        } else {
            // Ship not placed - keep it selected for tap-to-place
            this.endDrag(false);
        }
    }

    // End drag operation
    endDrag(wasPlaced = true): void {
        if (this.draggedShipElement) {
            this.draggedShipElement.classList.remove('dragging');

            // If not placed, mark as selected for tap-to-place mode
            if (!wasPlaced) {
                this.draggedShipElement.classList.add('selected');
            }
        }

        this.removeGhost();
        this.clearPlacementPreview();
        this.isDragging = false;

        // Only clear selection if the ship was placed
        if (wasPlaced) {
            this.draggedShip = null;
            this.draggedShipElement = null;
            if (this.onDragEndCallback) {
                this.onDragEndCallback();
            }
        } else {
            // Keep ship reference for rotation, but clear drag state
            this.selectedFromDock = this.draggedShip;
            this.selectedFromDockElement = this.draggedShipElement;
            this.draggedShip = null;
            this.draggedShipElement = null;
        }

        this.currentCell = null;
    }

    // Get cell element at screen position
    getCellAtPosition(clientX: number, clientY: number): HTMLElement | undefined {
        const elements = document.elementsFromPoint(clientX, clientY);
        return elements.find(el =>
            el.classList.contains('cell') &&
            this.placementBoard?.contains(el)
        ) as HTMLElement | undefined;
    }

    // Show/hide rotate button (legacy - now always visible in controls)
    showRotateButton(_show: boolean): void {
        // No longer needed - rotate button is always visible in controls
    }

    // Rotate the currently dragged ship
    rotateDraggedShip(): void {
        if (this.draggedShip) {
            this.draggedShip.rotate();
            this.updateGhostOrientation(this.draggedShip.horizontal);

            // Update the ship item preview in the dock
            if (this.draggedShipElement) {
                this.updateShipPreviewOrientation(this.draggedShipElement, this.draggedShip.horizontal, this.draggedShip);
            }

            // Update preview if over a cell
            if (this.currentCell && this.placementCallbacks) {
                const valid = this.placementCallbacks.canPlace(
                    this.draggedShip,
                    this.currentCell.x,
                    this.currentCell.y
                );
                this.showPlacementPreview(
                    this.draggedShip,
                    this.currentCell.x,
                    this.currentCell.y,
                    valid
                );
                this.setGhostValid(valid);
            }
        }
    }

    // Update ship preview orientation in the dock
    updateShipPreviewOrientation(element: HTMLElement, horizontal: boolean, ship: Ship | null = null): void {
        // Handle cell-based preview
        const preview = element.querySelector<HTMLElement>('.ship-preview');
        if (preview) {
            preview.style.flexDirection = horizontal ? 'row' : 'column';
        }

        // Handle silhouette preview - need to recreate for proper sizing
        const silhouette = element.querySelector<HTMLElement>('.ship-preview-silhouette');
        if (silhouette && ship && this.playerFactionColors) {
            const newSilhouette = shipGraphicsManager.createDockPreviewElement(ship, this.playerFactionColors);
            if (newSilhouette) {
                silhouette.replaceWith(newSilhouette);
            }
        }
    }

    // Show ghost for a ship selected from the board (tap-to-move mode)
    showSelectedShipGhost(ship: Ship, cellX: number, cellY: number): void {
        this.selectedShipForMove = ship;

        // Get the cell's position to place ghost there
        const cell = this.placementBoard!.querySelector(
            `.cell[data-x="${cellX}"][data-y="${cellY}"]`
        );

        if (cell) {
            const rect = cell.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            this.createGhost(ship, centerX, centerY);
        }

        // Show placement preview at the original position
        if (this.placementCallbacks) {
            const valid = this.placementCallbacks.canPlace(ship, cellX, cellY);
            this.showPlacementPreview(ship, cellX, cellY, valid);
        }

        // Set up pointer tracking for the selected ship
        if (!this.selectedShipPointerHandler) {
            this.selectedShipPointerHandler = (e: PointerEvent) => {
                if (this.selectedShipForMove && this.ghostElement) {
                    this.updateGhostPosition(e.clientX, e.clientY);

                    // Find cell under pointer and show preview
                    const cell = this.getCellAtPosition(e.clientX, e.clientY);
                    if (cell) {
                        const x = parseInt(cell.dataset.x!);
                        const y = parseInt(cell.dataset.y!);
                        if (this.placementCallbacks) {
                            const valid = this.placementCallbacks.canPlace(this.selectedShipForMove, x, y);
                            this.showPlacementPreview(this.selectedShipForMove, x, y, valid);
                            this.setGhostValid(valid);
                        }
                    }
                }
            };
            document.addEventListener('pointermove', this.selectedShipPointerHandler);
        }
    }

    // Hide the selected ship ghost
    hideSelectedShipGhost(): void {
        this.selectedShipForMove = null;
        this.removeGhost();
        this.clearPlacementPreview();

        if (this.selectedShipPointerHandler) {
            document.removeEventListener('pointermove', this.selectedShipPointerHandler);
            this.selectedShipPointerHandler = null;
        }
    }

    // Rotate the selected ship (for tap-to-move mode)
    rotateSelectedShip(ship: Ship): void {
        if (this.selectedShipForMove === ship) {
            ship.rotate();
            this.updateGhostOrientation(ship.horizontal);

            // Update dock preview
            const element = document.querySelector<HTMLElement>(`.ship-item[data-ship-id="${ship.id}"]`);
            if (element) {
                this.updateShipPreviewOrientation(element, ship.horizontal, ship);
            }
        }
    }

    // Rotate ship selected from dock (tap-to-place mode)
    rotateShipFromDock(): void {
        if (this.selectedFromDock) {
            this.selectedFromDock.rotate();

            // Update dock preview
            if (this.selectedFromDockElement) {
                this.updateShipPreviewOrientation(this.selectedFromDockElement, this.selectedFromDock.horizontal, this.selectedFromDock);
            }

            // Show feedback
            this.showToast(this.selectedFromDock.horizontal ? 'Horizontal' : 'Vertical');
        }
    }

    // Clear dock selection (called when ship is placed)
    clearDockSelection(): void {
        if (this.selectedFromDockElement) {
            this.selectedFromDockElement.classList.remove('selected');
        }
        this.selectedFromDock = null;
        this.selectedFromDockElement = null;
    }

    // Update ship list to reflect placement state
    updateShipList(ships: Ship[]): void {
        for (const ship of ships) {
            const item = document.querySelector<HTMLElement>(`.ship-item[data-ship-id="${ship.id}"]`);
            if (item) {
                item.classList.toggle('placed', ship.placed);
                item.draggable = !ship.placed;
            }
        }
    }

    // Refresh ship list graphics (silhouette vs classic) without recreating elements
    refreshShipListGraphics(ships: Ship[] | null | undefined): void {
        if (!ships) return;

        const useSilhouettes = shipGraphicsManager.isSilhouetteMode() && this.playerFactionColors;

        for (const ship of ships) {
            const item = document.querySelector<HTMLElement>(`.ship-item[data-ship-id="${ship.id}"]`);
            if (!item) continue;

            // Remove existing preview (either type)
            const existingPreview = item.querySelector('.ship-preview, .ship-preview-silhouette');
            if (!existingPreview) continue;

            let newPreview: HTMLElement | null = null;

            if (useSilhouettes) {
                // Create silhouette preview
                newPreview = shipGraphicsManager.createDockPreviewElement(ship, this.playerFactionColors);
            }

            if (!newPreview) {
                // Fall back to cell-based preview
                newPreview = document.createElement('div');
                newPreview.className = 'ship-preview';
                newPreview.style.flexDirection = ship.horizontal ? 'row' : 'column';
                for (let i = 0; i < ship.size; i++) {
                    const cell = document.createElement('div');
                    cell.className = 'ship-cell';
                    newPreview.appendChild(cell);
                }
            }
            // Note: silhouette orientation is handled by createDockPreviewElement

            // Replace the preview element
            existingPreview.replaceWith(newPreview);
        }
    }

    // Set up placement board interactions
    setupPlacementBoard(
        onDrop: (x: number, y: number) => void,
        onCellClick: (x: number, y: number) => void,
        onCellRightClick: (x: number, y: number) => void,
        canPlace: (ship: Ship, x: number, y: number) => boolean
    ): void {
        // Store callbacks for drag operations and delegated events
        this.placementCallbacks = {
            onDrop,
            onCellClick,
            onCellRightClick,
            canPlace
        };

        // Abort previous delegated listeners to prevent accumulation on re-call
        if (this._placementBoardAbort) this._placementBoardAbort.abort();
        this._placementBoardAbort = new AbortController();
        const signal = this._placementBoardAbort.signal;

        // Event delegation: one listener per board instead of per cell
        this.placementBoard!.addEventListener('click', (e) => {
            const cell = (e.target as Element).closest<HTMLElement>('.cell');
            if (!cell || this.isDragging) return;
            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);
            onCellClick(x, y);
        }, { signal });

        this.placementBoard!.addEventListener('contextmenu', (e) => {
            const cell = (e.target as Element).closest<HTMLElement>('.cell');
            if (!cell) return;
            e.preventDefault();
            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);
            onCellRightClick(x, y);
        }, { signal });

        // Keyboard rotation — abort previous listener to avoid accumulation
        if (this._keydownAbortController) {
            this._keydownAbortController.abort();
        }
        this._keydownAbortController = new AbortController();
        document.addEventListener('keydown', (e) => {
            if (e.key === 'r' || e.key === 'R') {
                if (this.isDragging) {
                    this.rotateDraggedShip();
                } else if (this.selectedShipForMove) {
                    this.rotateSelectedShip(this.selectedShipForMove);
                } else if (this.selectedFromDock) {
                    this.rotateShipFromDock();
                } else {
                    onCellRightClick(-1, -1); // Signal to rotate selected ship
                }
            }
        }, { signal: this._keydownAbortController.signal });

        // Rotate button in controls
        const rotateBtn = document.getElementById('btn-rotate');
        if (rotateBtn) {
            rotateBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.isDragging) {
                    this.rotateDraggedShip();
                } else if (this.selectedShipForMove) {
                    this.rotateSelectedShip(this.selectedShipForMove);
                } else if (this.selectedFromDock) {
                    this.rotateShipFromDock();
                } else {
                    onCellRightClick(-1, -1); // Signal to rotate selected ship
                }
            });
        }
    }

    // Show placement preview on hover
    showPlacementPreview(ship: Ship, x: number, y: number, valid: boolean): void {
        this.clearPlacementPreview();

        for (let i = 0; i < ship.size; i++) {
            const cx = ship.horizontal ? x + i : x;
            const cy = ship.horizontal ? y : y + i;

            if (cx >= 0 && cx < GRID_SIZE && cy >= 0 && cy < GRID_SIZE) {
                const cell = this.placementBoard!.querySelector(
                    `.cell[data-x="${cx}"][data-y="${cy}"]`
                );
                if (cell) {
                    cell.classList.add(valid ? 'valid-placement' : 'invalid-placement');
                }
            }
        }
    }

    // Clear placement preview
    clearPlacementPreview(): void {
        const cells = this.placementBoard!.querySelectorAll('.cell');
        for (const cell of cells) {
            cell.classList.remove('valid-placement', 'invalid-placement');
        }
    }

    // Render placement board with ships
    renderPlacementBoard(board: Board): void {
        const cells = this.placementBoard!.querySelectorAll<HTMLElement>('.cell');

        for (const cell of cells) {
            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);
            const hasShip = board.grid[y][x] !== null;

            cell.classList.toggle('ship', hasShip);
            cell.classList.remove('valid-placement', 'invalid-placement');
            cell.classList.remove('has-silhouette');
        }

        // Remove existing silhouettes
        this.placementBoard!.querySelectorAll('.ship-silhouette').forEach(el => el.remove());

        // Render ship silhouettes if enabled
        if (board.ships && this.playerFactionColors) {
            this.renderShipSilhouettes(board.ships, this.placementBoard);
        }
    }

    // Set up enemy board for firing
    setupEnemyBoard(onFire: (x: number, y: number) => void): void {
        // Abort previous listener to prevent accumulation on re-call
        if (this._enemyBoardAbort) this._enemyBoardAbort.abort();
        this._enemyBoardAbort = new AbortController();

        // Event delegation: one listener on the board container
        this.enemyBoard!.addEventListener('click', (e) => {
            const cell = (e.target as Element).closest<HTMLElement>('.cell');
            if (!cell) return;
            if (cell.classList.contains('hit') || cell.classList.contains('miss')) return;

            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);

            // Add firing animation
            cell.classList.add('firing');
            setTimeout(() => cell.classList.remove('firing'), 300);

            onFire(x, y);
        }, { signal: this._enemyBoardAbort.signal });
    }

    // Render your board during battle.
    // Only touches the classes this renderer owns (ship/hit/miss/shot/
    // decoy-placed) instead of resetting className wholesale: the old
    // reset-and-restore-allowlist approach cut off any animation or ability
    // marker class that wasn't on the allowlist (scanner reveals, heal
    // effects, silhouette-mode has-silhouette/has-debris, ...) whenever a
    // shot result triggered a re-render, which showed up as flicker.
    renderYourBoard(board: Board): void {
        const cells = this.yourBoard!.querySelectorAll<HTMLElement>('.cell');

        for (const cell of cells) {
            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);
            const hasShip = board.grid[y][x] !== null;
            const shot = board.getShotResult(x, y);

            const isDestroyedOrSunk = cell.classList.contains('destroyed') ||
                cell.classList.contains('sunk');
            const isCascading = cell.classList.contains('cascade-origin') ||
                cell.classList.contains('cascade-spread');

            cell.classList.toggle('ship', hasShip);
            cell.classList.remove('hit', 'miss', 'shot');
            if (shot) {
                // A shot on a decoy cell ends the decoy marker there
                cell.classList.remove('decoy-placed');
                if (!shot.hit) {
                    cell.classList.add('miss');
                } else if (!isDestroyedOrSunk && !isCascading) {
                    // Skip the hit marker on destroyed/sunk/exploding cells
                    // (it would overlap the sunk marker / cascade animation)
                    cell.classList.add('hit');
                }
            }
        }
    }

    // Render enemy board (only shows shots, not ships).
    // Like renderYourBoard, only touches renderer-owned classes (hit/miss/
    // shot) so ability markers and in-flight animations survive re-renders.
    renderEnemyBoard(shots: Map<string, EnemyShotMark>, sunkShips: SunkShipView[] = []): void {
        const cells = this.enemyBoard!.querySelectorAll<HTMLElement>('.cell');

        // First clear and mark shots
        for (const cell of cells) {
            const x = parseInt(cell.dataset.x!);
            const y = parseInt(cell.dataset.y!);
            const key = `${x},${y}`;

            const isDestroyedOrSunk = cell.classList.contains('destroyed') ||
                cell.classList.contains('sunk');
            const isCascading = cell.classList.contains('cascade-origin') ||
                cell.classList.contains('cascade-spread');

            cell.classList.remove('hit', 'miss', 'shot');
            if (shots.has(key)) {
                const result = shots.get(key)!;
                if (!result.hit) {
                    cell.classList.add('miss');
                } else if (!isDestroyedOrSunk && !isCascading) {
                    // Skip the hit marker on destroyed/sunk/exploding cells
                    // (it would overlap the sunk marker / cascade animation)
                    cell.classList.add('hit');
                }
                cell.classList.add('shot');
            }
        }

        // Mark sunk ships (but not during cascade animation)
        for (const ship of sunkShips) {
            for (const cellPos of ship.getCells()) {
                const cell = this.enemyBoard!.querySelector(
                    `.cell[data-x="${cellPos.x}"][data-y="${cellPos.y}"]`
                );
                if (cell) {
                    // Don't add sunk class during cascade animation (it would override the ::after burst)
                    const isAnimating = cell.classList.contains('cascade-origin') ||
                                        cell.classList.contains('cascade-spread');
                    if (!isAnimating) {
                        cell.classList.add('sunk');
                    }
                }
            }
        }
    }

    // Clear all game state from boards (for rematch)
    clearBattleBoards(): void {
        for (const board of [this.enemyBoard, this.yourBoard]) {
            board!.querySelectorAll('.ship-silhouette, .ship-debris').forEach(el => el.remove());
            const cells = board!.querySelectorAll('.cell');
            for (const cell of cells) {
                cell.className = 'cell';
            }
        }
    }

    // Recreate placement board grid (delegated listeners on the container survive)
    resetPlacementBoard(): void {
        this.createGrid(this.placementBoard!);
    }

    // Mark a cell as hit
    markHit(board: Board | null, x: number, y: number, isEnemy = false): void {
        const container = (isEnemy ? this.enemyBoard : this.yourBoard)!;
        const boardSection = container.closest<HTMLElement>('.board-section');
        const cell = container.querySelector<HTMLElement>(`.cell[data-x="${x}"][data-y="${y}"]`);
        if (cell) {
            // Reset and trigger animations
            cell.classList.remove('hit', 'shot', 'hit-anim');
            forceReflow(cell);
            cell.classList.add('hit', 'shot', 'hit-anim');

            // Remove animation class after it completes (guard against stale DOM)
            setTimeout(() => {
                if (cell.isConnected) cell.classList.remove('hit-anim');
            }, 600);

            // Screen shake effect on the board
            if (boardSection) {
                boardSection.classList.remove('shake');
                forceReflow(boardSection);
                boardSection.classList.add('shake');
                setTimeout(() => {
                    if (boardSection.isConnected) boardSection.classList.remove('shake');
                }, 400);
            }

            // Flash the board border
            container.style.boxShadow = '0 0 40px rgba(255, 100, 0, 0.8), inset 0 0 30px rgba(255, 50, 0, 0.3)';
            setTimeout(() => {
                if (container.isConnected) container.style.boxShadow = '';
            }, 300);
        }
    }

    // Mark a cell as miss
    markMiss(board: Board | null, x: number, y: number, isEnemy = false): void {
        const container = (isEnemy ? this.enemyBoard : this.yourBoard)!;
        const cell = container.querySelector<HTMLElement>(`.cell[data-x="${x}"][data-y="${y}"]`);
        if (cell) {
            // Reset and trigger animations
            cell.classList.remove('miss', 'shot', 'miss-anim');
            forceReflow(cell);
            cell.classList.add('miss', 'shot', 'miss-anim');

            // Remove animation class after it completes (guard against stale DOM)
            setTimeout(() => {
                if (cell.isConnected) cell.classList.remove('miss-anim');
            }, 600);

            // Subtle board glow on miss
            container.style.boxShadow = '0 0 25px rgba(51, 102, 255, 0.5), inset 0 0 20px rgba(51, 102, 255, 0.2)';
            setTimeout(() => {
                if (container.isConnected) container.style.boxShadow = '';
            }, 200);
        }
    }

    // Mark a ship as sunk with explosion cascade animation
    markSunk(ship: SunkShipView, isEnemy = false, lastHitCoords: Coord | null = null): void {
        const container = (isEnemy ? this.enemyBoard : this.yourBoard)!;
        const cells = ship.getCells();

        // Find the origin cell (last hit) - defaults to last cell in array
        let originIndex = cells.length - 1;
        if (lastHitCoords) {
            const foundIndex = cells.findIndex(c => c.x === lastHitCoords.x && c.y === lastHitCoords.y);
            if (foundIndex !== -1) originIndex = foundIndex;
        }

        // Calculate distances from origin for stagger timing
        const originCell = cells[originIndex];
        const cellsWithDistance = cells.map((cellPos, index) => ({
            cellPos,
            index,
            distance: Math.abs(cellPos.x - originCell.x) + Math.abs(cellPos.y - originCell.y)
        }));

        // Sort by distance so origin explodes first, then spreads outward
        cellsWithDistance.sort((a, b) => a.distance - b.distance);

        // Remove the ship silhouette immediately so it doesn't cover the animation
        // Silhouette is attached to the first cell of the ship
        const firstCellPos = cells[0];
        const firstCell = container.querySelector(
            `.cell[data-x="${firstCellPos.x}"][data-y="${firstCellPos.y}"]`
        );
        if (firstCell) {
            const silhouette = firstCell.querySelector<HTMLElement>('.ship-silhouette');
            if (silhouette) {
                // Fade out the silhouette
                silhouette.style.transition = 'opacity 0.3s ease-out';
                silhouette.style.opacity = '0';
                setTimeout(() => silhouette.remove(), 300);
            }
        }

        // Apply animations with staggered timing
        cellsWithDistance.forEach((item, staggerIndex) => {
            const cell = container.querySelector(
                `.cell[data-x="${item.cellPos.x}"][data-y="${item.cellPos.y}"]`
            );
            if (cell) {
                setTimeout(() => {
                    // Remove hit state and ship styling so animation shows
                    cell.classList.remove('hit', 'hit-anim', 'shot', 'ship', 'has-silhouette');

                    if (item.distance === 0) {
                        // Origin cell - the explosion starting point
                        cell.classList.add('cascade-origin');
                    } else {
                        // Spread cells - explosion propagates outward
                        cell.classList.add('cascade-spread');
                    }
                }, staggerIndex * 120); // 120ms stagger between cells
            }
        });

        // After all animations complete, apply destroyed state to all cells
        const totalAnimationTime = (cells.length * 120) + 600;
        this.sinkAnimationsEndAt = Math.max(this.sinkAnimationsEndAt, Date.now() + totalAnimationTime);
        setTimeout(() => {
            cells.forEach(cellPos => {
                const cell = container.querySelector(
                    `.cell[data-x="${cellPos.x}"][data-y="${cellPos.y}"]`
                );
                if (cell) {
                    cell.classList.remove('cascade-origin', 'cascade-spread', 'has-silhouette');
                    cell.classList.add('destroyed', 'sunk');

                    // Add debris for silhouette mode
                    const factionColors = isEnemy ? this.enemyFactionColors : this.playerFactionColors;
                    if (factionColors) {
                        this.renderDebris(cellPos.x, cellPos.y, factionColors, container);
                    }
                }
            });
        }, totalAnimationTime);
    }

    // Render ships status indicators
    renderShipsStatus(ships: ShipStatusView[], containerId: string, _showDestroyed = true): void {
        const container = document.getElementById(containerId)!;
        container.innerHTML = '';

        for (const ship of ships) {
            const status = document.createElement('div');
            status.className = `ship-status ${ship.isSunk() ? 'destroyed' : ''}`;

            const indicator = document.createElement('span');
            indicator.className = 'ship-status-indicator';

            const name = document.createElement('span');
            name.textContent = ship.class;

            status.appendChild(indicator);
            status.appendChild(name);
            container.appendChild(status);
        }
    }

    // Update turn indicator. flipBoards=false updates only the indicator
    // text/border — used for the optimistic WAITING state right after firing,
    // where flipping the boards and then flipping them back on the server's
    // turn-change would flash both boards on every shot (worst in blitz and
    // extra-shot turns, where the turn returns to the player immediately).
    updateTurnIndicator(isMyTurn: boolean, message: string | null = null, flipBoards = true): void {
        const indicator = document.getElementById('turn-indicator')!;
        const text = document.getElementById('turn-text')!;

        if (message) {
            text.textContent = message;
        } else {
            text.textContent = isMyTurn ? 'YOUR TURN' : 'ENEMY TURN';
        }

        indicator.style.borderColor = isMyTurn ?
            'var(--faction-primary)' : 'var(--text-secondary)';

        // Update board glow to indicate active turn
        if (flipBoards) {
            this.updateBoardTurnState(isMyTurn);
        }
    }

    // Update board visual state based on whose turn it is
    updateBoardTurnState(isMyTurn: boolean): void {
        if (!this.yourBoard || !this.enemyBoard) return;

        const activeBoard = isMyTurn ? this.enemyBoard : this.yourBoard;
        const inactiveBoard = isMyTurn ? this.yourBoard : this.enemyBoard;

        // Check if this is a turn change (board was previously inactive)
        const isTurnChange = activeBoard.classList.contains('inactive-turn');

        // Update classes
        activeBoard.classList.add('active-turn');
        activeBoard.classList.remove('inactive-turn');
        inactiveBoard.classList.remove('active-turn');
        inactiveBoard.classList.add('inactive-turn');

        // Add flash animation on turn change
        if (isTurnChange) {
            activeBoard.classList.add('turn-flash');
            setTimeout(() => {
                activeBoard.classList.remove('turn-flash');
            }, 400);
        }
    }

    // Show/hide screens
    showScreen(screenId: string): void {
        // Any explicit navigation supersedes a game-over screen still waiting
        // on a sink animation (e.g. leaving or a rematch starting).
        if (this.pendingScreenTimer) {
            clearTimeout(this.pendingScreenTimer);
            this.pendingScreenTimer = null;
        }

        const screens = document.querySelectorAll('.screen');
        for (const screen of screens) {
            screen.classList.remove('active');
        }

        const targetScreen = document.getElementById(screenId);
        if (targetScreen) {
            targetScreen.classList.add('active');
        }
    }

    // Set faction styling with dynamic colors
    setFactionStyle(faction: string, colors: FactionStyleColors | null = null): void {
        // Remove old faction classes from body
        document.body.className = document.body.className
            .split(' ')
            .filter(c => !c.startsWith('faction-'))
            .join(' ');

        // Normalize faction ID for CSS (replace underscores with hyphens)
        const cssFaction = faction.replace(/_/g, '-');
        document.body.classList.add(`faction-${cssFaction}`);

        // Set data-faction attribute on html element for CSS selectors
        document.documentElement.setAttribute('data-faction', cssFaction);

        // Preset factions have CSS rules in variables.css - don't override with inline styles
        const PRESET_FACTIONS = ['arcona', 'naga-sadow', 'plagueis', 'scholae', 'vizsla', 'taldryan', 'odan-urr'];

        if (PRESET_FACTIONS.includes(cssFaction)) {
            // Clear any inline styles so CSS variables take precedence
            document.documentElement.style.removeProperty('--faction-primary');
            document.documentElement.style.removeProperty('--faction-secondary');
            document.documentElement.style.removeProperty('--faction-glow');
            document.documentElement.style.removeProperty('--faction-bg');
        } else if (colors) {
            // Custom faction - apply inline styles
            document.documentElement.style.setProperty('--faction-primary', colors.primary);
            document.documentElement.style.setProperty('--faction-secondary', colors.secondary);
            document.documentElement.style.setProperty('--faction-glow', colors.glow || `${colors.primary}99`);
            document.documentElement.style.setProperty('--faction-bg', `${colors.primary}1a`);
        }
    }

    // Set opponent faction styling for enemy board
    setOpponentFactionStyle(colors: FactionStyleColors | null | undefined): void {
        if (colors) {
            document.documentElement.style.setProperty('--opponent-primary', colors.primary);
            document.documentElement.style.setProperty('--opponent-secondary', colors.secondary);
            document.documentElement.style.setProperty('--opponent-glow', colors.glow || `${colors.primary}99`);
            document.documentElement.style.setProperty('--opponent-bg', `${colors.primary}1a`);
        }
    }

    // Update player info in battle header (with HTML escaping for security)
    updateBattleHeader(
        yourName: string,
        yourFaction: string | null,
        enemyName: string,
        enemyFaction: string | null,
        enemyFactionData: ClientFaction | undefined,
        abilities: AbilitiesMap
    ): void {
        document.getElementById('your-name')!.textContent = yourName;
        document.getElementById('enemy-name')!.textContent = enemyName;

        const yourIcon = document.getElementById('your-faction-icon')!;
        const enemyIcon = document.getElementById('enemy-faction-icon')!;

        yourIcon.className = `faction-icon ${yourFaction}`;
        enemyIcon.className = `faction-icon ${enemyFaction}`;

        // Update enemy faction name and abilities
        const enemyFactionNameEl = document.getElementById('enemy-faction-name');
        const enemyAbilitiesEl = document.getElementById('enemy-abilities');

        if (enemyFactionNameEl && enemyFactionData) {
            enemyFactionNameEl.textContent = enemyFactionData.name || enemyFaction;
        }

        if (enemyAbilitiesEl && enemyFactionData && abilities) {
            const factionAbilities = (enemyFactionData.abilities || [])
                .map(abilityId => abilities[abilityId])
                .filter(Boolean);

            // Clear existing content
            enemyAbilitiesEl.innerHTML = '';

            if (factionAbilities.length > 0) {
                factionAbilities.forEach(ability => {
                    const typeLabel = ability.type === 'passive' ? 'Passive' : 'Active';
                    const typeClass = ability.type === 'passive' ? 'passive' : 'active';
                    const description = ability.description || '';

                    const abilityDiv = document.createElement('div');
                    abilityDiv.className = `enemy-ability ${typeClass}`;
                    abilityDiv.setAttribute('data-tooltip', description);

                    const typeSpan = document.createElement('span');
                    typeSpan.className = 'ability-type';
                    typeSpan.textContent = `${typeLabel}:`;

                    const nameSpan = document.createElement('span');
                    nameSpan.className = 'ability-name';
                    nameSpan.textContent = ability.name;

                    const tooltipSpan = document.createElement('span');
                    tooltipSpan.className = 'tooltip';
                    tooltipSpan.textContent = description;

                    abilityDiv.appendChild(typeSpan);
                    abilityDiv.appendChild(nameSpan);
                    abilityDiv.appendChild(tooltipSpan);
                    enemyAbilitiesEl.appendChild(abilityDiv);
                });
            }
        }
    }

    // Show game over screen
    showGameOver(won: boolean, stats: GameOverStats, playerInfo: GameOverPlayerInfo | null = null): void {
        const title = document.getElementById('gameover-title')!;
        const message = document.getElementById('gameover-message')!;

        title.textContent = won ? 'VICTORY' : 'DEFEAT';
        title.className = `gameover-title ${won ? '' : 'defeat'}`;

        if (stats.isBlitz) {
            message.textContent = won ?
                'All targets eliminated!' :
                'Your fleet has been destroyed.';
        } else {
            message.textContent = won ?
                'The enemy fleet has been destroyed!' :
                'Your fleet has been destroyed.';
        }

        // Update stats
        document.getElementById('stat-shots')!.textContent = String(stats.shotsFired);
        document.getElementById('stat-accuracy')!.textContent = `${stats.accuracy}%`;
        document.getElementById('stat-ships-lost')!.textContent = String(stats.shipsLost);
        document.getElementById('stat-turns')!.textContent = String(stats.turns || 0);
        document.getElementById('stat-duration')!.textContent = stats.duration || '0:00';

        // Update score display (SP wins only — a loss has no score or stars)
        const scoreDiv = document.getElementById('gameover-score')!;
        if (won && stats.score !== undefined && stats.score !== null) {
            scoreDiv.classList.remove('hidden');
            document.getElementById('score-number')!.textContent = String(stats.score);

            // Update star rating
            const stars = document.querySelectorAll('#score-stars .star');
            const starRating = stats.starRating || 0;
            stars.forEach((star, i) => {
                star.classList.toggle('earned', i < starRating);
            });
        } else {
            scoreDiv.classList.add('hidden');
        }

        // Update time trial result display
        const ttDiv = document.getElementById('gameover-time-trial');
        if (ttDiv) {
            if (stats.isTimeTrial && won) {
                ttDiv.classList.remove('hidden');
                const secs = stats.durationSeconds || 0;
                const mins = Math.floor(secs / 60);
                const secPart = secs % 60;
                const timeStr = `${mins}:${secPart.toString().padStart(2, '0')}`;
                document.getElementById('time-trial-result-time')!.textContent = timeStr;
                // Label will be updated by main.js after checking personal best
                document.getElementById('time-trial-result-label')!.textContent = 'TIME TRIAL';
            } else {
                ttDiv.classList.add('hidden');
            }
        }

        // Update match display
        const matchDiv = document.getElementById('gameover-match')!;
        if (playerInfo) {
            matchDiv.style.display = '';

            const winnerName = won ? playerInfo.playerName : playerInfo.opponentName;
            const winnerFaction = won ? playerInfo.playerFactionName : playerInfo.opponentFactionName;
            const winnerFactionId = won ? playerInfo.playerFactionId : playerInfo.opponentFactionId;
            const loserName = won ? playerInfo.opponentName : playerInfo.playerName;
            const loserFaction = won ? playerInfo.opponentFactionName : playerInfo.playerFactionName;
            const loserFactionId = won ? playerInfo.opponentFactionId : playerInfo.playerFactionId;

            document.getElementById('winner-name')!.textContent = winnerName;
            document.getElementById('winner-faction')!.textContent = winnerFaction;
            document.getElementById('loser-name')!.textContent = loserName;
            document.getElementById('loser-faction')!.textContent = loserFaction;

            // Set faction logos
            const winnerLogo = document.getElementById('winner-faction-logo')!;
            const loserLogo = document.getElementById('loser-faction-logo')!;
            if (winnerFactionId) {
                winnerLogo.style.backgroundImage = `url('/djbship/images/logos/${encodeURIComponent(winnerFactionId)}.svg?v=5')`;
                winnerLogo.style.display = '';
                winnerLogo.setAttribute('role', 'img');
                winnerLogo.setAttribute('aria-label', `${winnerFaction} emblem`);
            } else {
                winnerLogo.style.display = 'none';
            }
            if (loserFactionId) {
                loserLogo.style.backgroundImage = `url('/djbship/images/logos/${encodeURIComponent(loserFactionId)}.svg?v=5')`;
                loserLogo.style.display = '';
                loserLogo.setAttribute('role', 'img');
                loserLogo.setAttribute('aria-label', `${loserFaction} emblem`);
            } else {
                loserLogo.style.display = 'none';
            }
        } else {
            matchDiv.style.display = 'none';
        }

        // Let the final sink cascade finish on the board before swapping screens.
        const wait = this.sinkAnimationsEndAt - Date.now();
        if (wait > 0) {
            if (this.pendingScreenTimer) clearTimeout(this.pendingScreenTimer);
            this.pendingScreenTimer = setTimeout(() => {
                this.pendingScreenTimer = null;
                this.showScreen('gameover-screen');
            }, wait);
        } else {
            this.showScreen('gameover-screen');
        }
    }

    // Show toast notification. Each show restarts the hide timer, so a toast
    // shown while an earlier one is up gets its full duration (previously the
    // earlier toast's timer hid it early). Errors linger a little longer by
    // default since they usually need reading.
    showToast(message: string, isError = false, duration = isError ? TOAST_ERROR_MS : TOAST_MS): void {
        const toast = document.getElementById('toast');
        if (!toast) return;
        if (this.toastTimer) clearTimeout(this.toastTimer);
        toast.textContent = message;
        toast.classList.toggle('error', isError);
        toast.classList.remove('hidden');

        this.toastTimer = setTimeout(() => {
            this.toastTimer = null;
            toast.classList.add('hidden');
        }, duration);
    }

    // Update ready button state
    updateReadyButton(allPlaced: boolean): void {
        const btn = document.getElementById('btn-ready') as HTMLButtonElement;
        btn.disabled = !allPlaced;
    }

    // Update opponent status
    updateOpponentStatus(name: string, ready: boolean, visible = true): void {
        const container = document.getElementById('opponent-status')!;
        container.classList.toggle('hidden', !visible);

        document.getElementById('opponent-name')!.textContent = name;
        document.getElementById('opponent-ready-status')!.textContent =
            ready ? 'Ready!' : 'Placing ships...';
    }

    // Animate repair effect on a cell
    animateRepair(x: number, y: number, boardElement: HTMLElement): void {
        const cell = boardElement.querySelector(`[data-x="${x}"][data-y="${y}"]`);
        if (!cell) return;

        // Remove hit/miss classes and add repair animation
        cell.classList.remove('hit', 'miss');
        cell.classList.add('repair-anim');

        // Remove debris (for silhouette mode)
        this.removeDebris(x, y, boardElement);

        // Remove animation class after it completes
        setTimeout(() => {
            cell.classList.remove('repair-anim');
        }, 800);
    }

    // Clear shot marker from tracking board (for opponent repair)
    clearShotMarker(x: number, y: number, boardElement: HTMLElement): void {
        const cell = boardElement.querySelector(`[data-x="${x}"][data-y="${y}"]`);
        if (!cell) return;

        cell.classList.remove('hit', 'miss', 'shot');
    }

    // Set faction colors for ship graphics
    setFactionColors(
        playerColors: FactionStyleColors | null | undefined,
        enemyColors: FactionStyleColors | null | undefined = null
    ): void {
        if (playerColors) {
            this.playerFactionColors = playerColors;
        }
        if (enemyColors) {
            this.enemyFactionColors = enemyColors;
        }
    }

    // Render ship silhouettes on player's board
    renderShipSilhouettes(ships: Ship[], boardElement: HTMLElement | null = null): void {
        console.log('[Renderer] renderShipSilhouettes called', {
            silhouetteMode: shipGraphicsManager.isSilhouetteMode(),
            hasColors: !!this.playerFactionColors,
            shipCount: ships?.length
        });
        if (!shipGraphicsManager.isSilhouetteMode()) return;
        if (!this.playerFactionColors) return;

        const board = boardElement || this.yourBoard;
        if (!board) return;

        // Remove existing silhouettes
        board.querySelectorAll('.ship-silhouette').forEach(el => el.remove());

        for (const ship of ships) {
            if (!ship.placed) continue;

            console.log('[Renderer] Rendering ship:', ship.class, 'at', ship.x, ship.y, 'horizontal:', ship.horizontal);

            // Render silhouette on first cell only (it spans all cells)
            const firstCell = board.querySelector(
                `[data-x="${ship.x}"][data-y="${ship.y}"]`
            );

            if (firstCell) {
                const silhouette = shipGraphicsManager.createShipElement(
                    ship,
                    this.playerFactionColors,
                    0,
                    firstCell
                );

                console.log('[Renderer] Created silhouette:', !!silhouette);

                if (silhouette) {
                    firstCell.classList.add('has-silhouette');
                    firstCell.appendChild(silhouette);
                }

                // Mark other cells as having silhouette (to hide default bg)
                for (let i = 1; i < ship.size; i++) {
                    const cellX = ship.horizontal ? ship.x + i : ship.x;
                    const cellY = ship.horizontal ? ship.y : ship.y + i;
                    const cell = board.querySelector(`[data-x="${cellX}"][data-y="${cellY}"]`);
                    if (cell) {
                        cell.classList.add('has-silhouette');
                    }
                }
            }
        }
    }

    // Render debris on a specific cell
    renderDebris(x: number, y: number, factionColors: FactionStyleColors, boardElement: Element | null = null): void {
        if (!shipGraphicsManager.isSilhouetteMode()) return;

        const board = (boardElement || this.yourBoard)!;
        const cell = board.querySelector(`[data-x="${x}"][data-y="${y}"]`);
        if (!cell) return;

        // Don't add debris if already present
        if (cell.querySelector('.ship-debris')) return;

        const debris = shipGraphicsManager.createDebrisElement(factionColors);
        if (debris) {
            cell.classList.add('has-debris');
            cell.appendChild(debris);
        }
    }

    // Remove debris from a cell (for repair ability)
    removeDebris(x: number, y: number, boardElement: HTMLElement | null = null): void {
        const board = (boardElement || this.yourBoard)!;
        const cell = board.querySelector(`[data-x="${x}"][data-y="${y}"]`);
        if (!cell) return;

        const debris = cell.querySelector('.ship-debris');
        if (debris) {
            debris.remove();
            cell.classList.remove('has-debris');
        }
    }

    // Refresh boards when graphics mode changes
    refreshBoards(): void {
        const isSilhouette = shipGraphicsManager.isSilhouetteMode();
        const inBattle = gameState.gameStarted && !gameState.gameOver;
        const inPlacement = gameState.playerBoard && !gameState.gameStarted;

        console.log('[Renderer] refreshBoards called', {
            isSilhouette,
            inBattle,
            inPlacement,
            hasShips: !!gameState.ships
        });

        // Re-render player board if in battle
        if (inBattle && gameState.playerBoard && this.yourBoard) {
            // Remove all silhouettes and debris first
            this.yourBoard.querySelectorAll('.ship-silhouette, .ship-debris').forEach(el => el.remove());
            this.yourBoard.querySelectorAll('.has-silhouette, .has-debris').forEach(cell => {
                cell.classList.remove('has-silhouette', 'has-debris');
            });

            this.renderYourBoard(gameState.playerBoard);

            // Re-render silhouettes if in silhouette mode
            if (isSilhouette && gameState.ships) {
                this.renderShipSilhouettes(gameState.ships, this.yourBoard);

                // Re-render debris for hit cells
                if (this.playerFactionColors) {
                    for (let y = 0; y < GRID_SIZE; y++) {
                        for (let x = 0; x < GRID_SIZE; x++) {
                            const shot = gameState.playerBoard.getShotResult(x, y);
                            if (shot && shot.hit) {
                                this.renderDebris(x, y, this.playerFactionColors, this.yourBoard);
                            }
                        }
                    }
                }
            }
        }

        // Re-render placement board if in placement phase
        if (inPlacement && this.placementBoard) {
            // Remove all silhouettes first
            this.placementBoard.querySelectorAll('.ship-silhouette').forEach(el => el.remove());
            this.placementBoard.querySelectorAll('.has-silhouette').forEach(cell => {
                cell.classList.remove('has-silhouette');
            });

            this.renderPlacementBoard(gameState.playerBoard);

            // Also refresh the ship list graphics
            this.refreshShipListGraphics(gameState.ships);
        }
    }
}

// Singleton renderer instance
export const renderer = new Renderer();
